import { useEffect, useMemo, useRef } from 'react';
import { Link, useParams } from 'react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';

import { fetchCustomerDetail } from '#/api/crmMgmt/customer/fetchDetail';
import {
  fetchCustomerSelectOptions,
  mergeCustomerSelectOption,
} from '#/api/crmMgmt/customer/fetchSelectOptions';
import { createProduct } from '#/api/crmMgmt/product/create';
import { fetchProductDetail } from '#/api/crmMgmt/product/fetchDetail';
import { updateProduct } from '#/api/crmMgmt/product/update';
import { ROUTES } from '#/config/routes';
import useCreateEditSave from '#/hooks/useCreateEditSave';
import useFormValidationSummary from '#/hooks/useFormValidationSummary';
import { Button } from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingTextarea from '#/modules/commonComponents/form/FloatingTextarea';
import FloatingSelect from '#/modules/commonComponents/form/select';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import ProductExtraItemsEditor from '#/modules/crmMgmt/product/components/ProductExtraItemsEditor';
import ProductMaterialsEditor from '#/modules/crmMgmt/product/components/ProductMaterialsEditor';
import {
  getProductApiErrorMessage,
  isProductVersionConflict,
} from '#/modules/crmMgmt/product/helper/apiError';
import {
  buildProductWritePayload,
  mapProductDetailToForm,
} from '#/modules/crmMgmt/product/helper/buildProductWritePayload';
import {
  PRODUCT_FIELD_LABELS,
  resolveProductFieldLabel,
} from '#/modules/crmMgmt/product/helper/formValidationLabels';
import { formatProductStatusLabel } from '#/modules/crmMgmt/product/helper/productStatus';
import {
  PRODUCT_FORM_DEFAULT_VALUES,
  productFormSchema,
} from '#/modules/crmMgmt/product/schemas/productSchema';
import PageShell from '#/modules/infra/layout/PageShell';
import {
  customerDetailQueryKey,
  lookupQueryKey,
  productDetailQueryKey,
  productListQueryKeyPrefix,
} from '#/utils/queryKeys';

const CreateEditProductPage = () => {
  const { id } = useParams();
  const updatedAtRef = useRef(null);
  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
    customMessages,
  } = useFormValidationSummary();

  const detailQuery = useQuery({
    queryKey: productDetailQueryKey(id),
    queryFn: () => fetchProductDetail(id),
    enabled: Boolean(id),
  });

  const boundCustomerId = detailQuery.data?.data?.customerId;

  const boundCustomerQuery = useQuery({
    queryKey: customerDetailQueryKey(boundCustomerId),
    queryFn: () => fetchCustomerDetail(boundCustomerId),
    enabled: Boolean(boundCustomerId),
  });

  const customerOptionsQuery = useQuery({
    queryKey: lookupQueryKey('customerSelect'),
    queryFn: fetchCustomerSelectOptions,
  });

  const { isEdit, isPending, save } = useCreateEditSave({
    id,
    createFn: createProduct,
    updateFn: updateProduct,
    buildPayload: (values) => buildProductWritePayload(values, {
      updatedAt: id ? updatedAtRef.current : null,
    }),
    listQueryKeyPrefix: productListQueryKeyPrefix,
    detailQueryKey: productDetailQueryKey,
    getDetailPath: ROUTES.productDetail,
    afterUpdateNavigateTo: id ? ROUTES.productDetail(id) : null,
    getErrorMessage: (error) => getProductApiErrorMessage(error, '儲存失敗'),
    isVersionConflict: isProductVersionConflict,
    onVersionConflict: () => detailQuery.refetch(),
  });

  const {
    register,
    handleSubmit,
    reset,
    control,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(productFormSchema),
    defaultValues: PRODUCT_FORM_DEFAULT_VALUES,
  });

  useEffect(() => {
    const detail = detailQuery.data?.data;
    if (!detail) {
      return;
    }

    updatedAtRef.current = detail.updatedAt ?? detail.record?.updatedAt ?? null;
    resetValidationSummary();
    reset(mapProductDetailToForm(detail));
  }, [detailQuery.data, reset, resetValidationSummary]);

  const customerOptions = useMemo(() => {
    const options = mergeCustomerSelectOption(
      customerOptionsQuery.data ?? [],
      boundCustomerQuery.data?.data,
    );
    return [{ value: '', label: '請選擇' }, ...options];
  }, [customerOptionsQuery.data, boundCustomerQuery.data?.data]);

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    save(values);
  }

  const pageTitle = isEdit ? '編輯產品' : '新增產品';
  const isPageLoading = isEdit && detailQuery.isLoading;
  const isDetailNotFound = isEdit && detailQuery.isError;
  const detailStatus = detailQuery.data?.data?.status;

  return (
    <PageShell variant="form">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header justify-between">
            <div className="flex items-center gap-2">
              <h1 className="page_title mb-0!">
                <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              </h1>
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.product}>返回列表</Link>
              </Button>
            </div>
            {isEdit ? (
              <p className="text-sm text-muted-foreground">
                狀態：{formatProductStatusLabel(detailStatus)}
              </p>
            ) : null}
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : isDetailNotFound ? (
            <p className="text-sm text-destructive">
              {getProductApiErrorMessage(detailQuery.error, '找不到產品資料')}
            </p>
          ) : (
            <>
              <FormValidationSummary
                show={isSubmitAttempted}
                errors={errors}
                fieldLabels={PRODUCT_FIELD_LABELS}
                fieldLabelResolver={resolveProductFieldLabel}
                customMessages={customMessages}
              />
              <form
                className="sheet create_edit create_edit_form create_edit_form--page-scroll"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                <div className="create_edit_form__body divide-y divide-gray-500">
                  <section className="pb-5">
                    <div className="form-block">
                      <h2 className="form-block__title">基本資料</h2>
                    </div>
                    <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                      <FloatingInput
                        id="productNo"
                        label="產品代號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="name"
                        label="名稱"
                        register={register}
                        errors={errors}
                      />
                      <FloatingSelect
                        id="customerId"
                        label="客戶（可以不選）"
                        options={customerOptions}
                        control={control}
                        errors={errors}
                        isClearable
                        isDisabled={customerOptionsQuery.isLoading}
                        noOptionsMessage="無可用客戶"
                      />
                      <FloatingInput
                        id="customerProductNo"
                        label="客戶料號"
                        register={register}
                        errors={errors}
                      />
                      <FloatingInput
                        id="salesUnitName"
                        label="銷售單位"
                        register={register}
                        errors={errors}
                      />
                    </div>
                    <FloatingTextarea
                      id="remark"
                      label="備註"
                      register={register}
                      errors={errors}
                    />
                  </section>

                  <ProductMaterialsEditor
                    control={control}
                    register={register}
                    errors={errors}
                    getValues={getValues}
                  />

                  <ProductExtraItemsEditor
                    control={control}
                    register={register}
                    errors={errors}
                  />
                </div>

                <div className="create_edit_form__actions flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.product}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={isPending}>
                    {isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditProductPage;
